import React, { useState, useCallback, useRef, useEffect } from 'react';
import BarcodeScanner from '../components/BarcodeScanner';
import SERVER_CONFIG from '../services/config';
import '../styles/SearchModal.css';

const API_URL = SERVER_CONFIG.apiUrl;

// Извлекает CIS из сырого DataMatrix кода
function extractCis(code) {
  try {
    const url = new URL(code);
    const cis = url.searchParams.get('cis');
    if (cis) return cis;
  } catch (_) {}
  const m = code.match(/^(01\d{14}21.{13})/);
  if (m) return m[1];
  return code;
}

function MedicineRevisionModal({ isOpen, onClose, onDone }) {
  const [step, setStep] = useState('scan'); // 'scan' | 'group' | 'cis_scan'
  const [scannedBarcode, setScannedBarcode] = useState('');
  const [groupData, setGroupData] = useState(null);
  const [bkQuantity, setBkQuantity] = useState('');
  const [bkSaved, setBkSaved] = useState(false);
  const [confirmedCis, setConfirmedCis] = useState(new Set());
  const [lastScanMsg, setLastScanMsg] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedMsg, setSavedMsg] = useState('');
  const [barcodeInput, setBarcodeInput] = useState('');
  const [scannerActive, setScannerActive] = useState(true);

  const token = localStorage.getItem('token');

  // Скрытый input для сканера (HID-устройства)
  const hiddenInputRef = useRef(null);
  const hiddenBufferRef = useRef('');
  const hiddenTimerRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => hiddenInputRef.current?.focus(), 100);
    }
  }, [isOpen, step]);

  const reset = () => {
    setStep('scan');
    setScannedBarcode('');
    setGroupData(null);
    setBkQuantity('');
    setBkSaved(false);
    setConfirmedCis(new Set());
    setLastScanMsg('');
    setSavedMsg('');
    setBarcodeInput('');
    setScannerActive(true);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const lookupGroup = async (code) => {
    setScannerActive(false);
    setLoading(true);
    setSavedMsg('');
    try {
      const res = await fetch(API_URL + '/inventory/barcode-group/' + encodeURIComponent(code), {
        headers: { Authorization: 'Bearer ' + token },
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();

      if (data.total === 0) {
        setSavedMsg('Препарат с таким штрихкодом не найден в базе');
        setScannerActive(true);
        setLoading(false);
        return;
      }

      setScannedBarcode(code);
      setGroupData(data);
      const bkTotal = data.bk_group.reduce((s, i) => s + (i.quantity || 0), 0);
      setBkQuantity(String(bkTotal));
      setBkSaved(false);
      setConfirmedCis(new Set());
      setLastScanMsg('');
      setStep('group');
    } catch (err) {
      setSavedMsg('Ошибка поиска. Попробуйте ещё раз.');
      setScannerActive(true);
    } finally {
      setLoading(false);
    }
  };

  // Обработчик сканирования камерой
  const handleBarcodeDetectedRef = useRef(null);
  handleBarcodeDetectedRef.current = ({ code, isQR }) => {
    if (step === 'scan') {
      if (isQR) {
        setSavedMsg('Отсканирован QR/ЧЗ код. Нужен штрихкод (линейный).');
        setTimeout(() => setSavedMsg(''), 2000);
      } else {
        lookupGroup(code);
      }
    } else if (step === 'cis_scan') {
      if (isQR) {
        handleCisScan(code);
      } else {
        setLastScanMsg('Нужен QR/ЧЗ код, а не штрихкод');
        setTimeout(() => setLastScanMsg(''), 1500);
      }
    }
  };
  const handleBarcodeDetected = useCallback((evt) => {
    handleBarcodeDetectedRef.current(evt);
  }, []);

  // Обработчик HID-сканера (клавиатурный ввод)
  const handleHidInput = (e) => {
    hiddenBufferRef.current += e.target.value;
    e.target.value = '';
    clearTimeout(hiddenTimerRef.current);
    hiddenTimerRef.current = setTimeout(() => {
      const code = hiddenBufferRef.current.trim();
      hiddenBufferRef.current = '';
      if (!code) return;
      if (step === 'scan') {
        lookupGroup(code);
      } else if (step === 'cis_scan') {
        handleCisScan(code);
      }
    }, 120);
  };

  const handleCisScan = (rawCode) => {
    const cis = extractCis(rawCode);
    if (!groupData) return;
    const match = groupData.cis_group.find(
      item => item.cis === cis || item.cis === rawCode
    );
    if (match) {
      setConfirmedCis(prev => new Set([...prev, match.cis]));
      setLastScanMsg('✅ ' + match.name);
    } else {
      setLastScanMsg('⚠️ Не найдено в группе');
    }
    setTimeout(() => setLastScanMsg(''), 1500);
  };

  const handleManualBarcode = () => {
    const code = barcodeInput.trim();
    if (!code) return;
    setBarcodeInput('');
    if (step === 'scan') lookupGroup(code);
    else if (step === 'cis_scan') handleCisScan(code);
  };

  const saveBkQuantity = async () => {
    if (!groupData?.bk_group?.length) return;
    setSaving(true);
    try {
      const bkItem = groupData.bk_group[0];
      const res = await fetch(API_URL + '/inventory/items/' + bkItem.sync_id + '/quantity', {
        method: 'PATCH',
        headers: { Authorization: 'Bearer ' + token, 'Content-Type': 'application/json' },
        body: JSON.stringify({ quantity: parseInt(bkQuantity) || 0 }),
      });
      if (!res.ok) throw new Error();
      setBkSaved(true);
      setSavedMsg('');
      if (onDone) onDone();
    } catch {
      setSavedMsg('Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  const deleteMissingCis = async () => {
    const toDelete = groupData.cis_group.filter(item => !confirmedCis.has(item.cis));
    if (!toDelete.length) return;
    setSaving(true);
    try {
      for (const item of toDelete) {
        await fetch(API_URL + '/inventory/items/' + item.sync_id, {
          method: 'DELETE',
          headers: { Authorization: 'Bearer ' + token },
        });
      }
      setGroupData(prev => ({
        ...prev,
        cis_group: prev.cis_group.filter(item => confirmedCis.has(item.cis)),
      }));
      setSavedMsg(`Удалено ${toDelete.length} зап.`);
      setStep('group');
      if (onDone) onDone();
    } catch {
      setSavedMsg('Ошибка удаления');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const cisTotal = groupData?.cis_group?.length || 0;
  const cisConfirmed = confirmedCis.size;
  const cisMissing = cisTotal - cisConfirmed;
  const bkTotal = groupData?.bk_group?.reduce((s, i) => s + (i.quantity || 0), 0) || 0;

  return (
    <div className="sm-overlay" onClick={e => e.target === e.currentTarget && handleClose()}>
      <div className="sm-modal">
        {/* Скрытый input для HID-сканера */}
        <input
          ref={hiddenInputRef}
          style={{ position: 'absolute', opacity: 0, width: 1, height: 1, pointerEvents: 'none' }}
          onInput={handleHidInput}
          readOnly={false}
        />

        <div className="sm-header">
          <span className="sm-title">Ревизия лекарств</span>
          <button className="sm-close" onClick={handleClose}>✕</button>
        </div>

        {/* ── Шаг 1: Сканирование ШК ── */}
        {step === 'scan' && (
          <div className="sm-body">
            <BarcodeScanner
              active={scannerActive && !loading}
              onDetected={handleBarcodeDetected}
            />
            <div className="sm-manual-row" style={{ marginTop: 12 }}>
              <input
                className="sm-input"
                placeholder="Введите штрихкод вручную"
                value={barcodeInput}
                onChange={e => setBarcodeInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleManualBarcode()}
              />
              <button className="sm-btn sm-btn-search" onClick={handleManualBarcode} disabled={!barcodeInput.trim()}>
                Найти
              </button>
            </div>
            {loading && <div className="sm-status">Поиск…</div>}
            {savedMsg && <div className="sm-status sm-status-warn">{savedMsg}</div>}
            <div className="sm-hint" style={{ marginTop: 16, color: '#888', fontSize: 13 }}>
              Сканируйте штрихкод препарата
            </div>
          </div>
        )}

        {/* ── Шаг 2: Группа ── */}
        {step === 'group' && groupData && (
          <div className="sm-body">
            <div className="sm-found-name">{groupData.name}</div>
            <div style={{ color: '#888', fontSize: 13, marginBottom: 16 }}>
              ШК: {scannedBarcode}
            </div>

            {/* ШК-группа */}
            {groupData.bk_group.length > 0 && (
              <div className="rev-group-block">
                <div className="rev-group-header">
                  <span className="rev-badge rev-badge-bk">ШК</span>
                  <span className="rev-group-label">
                    В базе: {bkTotal} шт
                  </span>
                  {bkSaved && <span style={{ color: '#66bb66', fontSize: 13 }}>✓ сохранено</span>}
                </div>
                <div className="rev-qty-row">
                  <span style={{ color: '#aaa', fontSize: 13 }}>По факту:</span>
                  <input
                    className="sm-input rev-qty-input"
                    type="number"
                    min="0"
                    value={bkQuantity}
                    onChange={e => { setBkQuantity(e.target.value); setBkSaved(false); }}
                    onKeyDown={e => e.key === 'Enter' && saveBkQuantity()}
                  />
                  <button
                    className={'sm-btn ' + (bkSaved ? 'sm-btn-secondary' : 'sm-btn-save')}
                    onClick={saveBkQuantity}
                    disabled={saving || bkSaved}
                  >
                    {bkSaved ? '✓' : 'Сохранить'}
                  </button>
                </div>
              </div>
            )}

            {/* ЧЗ-группа */}
            {groupData.cis_group.length > 0 && (
              <div className="rev-group-block">
                <div className="rev-group-header">
                  <span className="rev-badge rev-badge-chz">ЧЗ</span>
                  <span className="rev-group-label">
                    В базе: {cisTotal} шт
                  </span>
                  {cisConfirmed > 0 && (
                    <span style={{ color: '#66bb66', fontSize: 13 }}>
                      ✅ {cisConfirmed} из {cisTotal}
                    </span>
                  )}
                </div>
                {cisConfirmed > 0 && (
                  <div className="rev-cis-summary">
                    {groupData.cis_group.map(item => (
                      <div key={item.sync_id} className={'rev-cis-row ' + (confirmedCis.has(item.cis) ? 'rev-cis-ok' : 'rev-cis-missing')}>
                        {confirmedCis.has(item.cis) ? '✅' : '❌'} {item.name}
                        {item.expiry_date ? ' · до ' + item.expiry_date.slice(0, 7) : ''}
                      </div>
                    ))}
                  </div>
                )}
                <div className="rev-cis-btn-row">
                  <button
                    className="sm-btn sm-btn-search"
                    onClick={() => { setStep('cis_scan'); setSavedMsg(''); }}
                  >
                    {cisConfirmed === 0 ? 'Сканировать ЧЗ' : 'Продолжить сканирование'}
                  </button>
                  {cisMissing > 0 && cisConfirmed > 0 && (
                    <button
                      className="sm-btn sm-btn-danger"
                      onClick={deleteMissingCis}
                      disabled={saving}
                    >
                      Удалить {cisMissing} лишн.
                    </button>
                  )}
                  {cisMissing === 0 && cisConfirmed === cisTotal && cisTotal > 0 && (
                    <span style={{ color: '#66bb66', fontSize: 13 }}>✓ все подтверждены</span>
                  )}
                </div>
              </div>
            )}

            {savedMsg && <div className="sm-status">{savedMsg}</div>}

            <button
              className="sm-btn sm-btn-secondary"
              style={{ marginTop: 20 }}
              onClick={reset}
            >
              ← Следующий препарат
            </button>
          </div>
        )}

        {/* ── Шаг 3: Сканирование ЧЗ кодов ── */}
        {step === 'cis_scan' && groupData && (
          <div className="sm-body">
            <div className="sm-found-name">{groupData.name}</div>
            <div className="rev-cis-progress">
              <span className="rev-badge rev-badge-chz">ЧЗ</span>
              <span style={{ marginLeft: 8 }}>
                Подтверждено {cisConfirmed} из {cisTotal}
              </span>
            </div>

            <BarcodeScanner
              active={true}
              onDetected={handleBarcodeDetected}
            />

            <div className="sm-manual-row" style={{ marginTop: 8 }}>
              <input
                className="sm-input"
                placeholder="Введите ЧЗ код вручную"
                value={barcodeInput}
                onChange={e => setBarcodeInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleManualBarcode()}
              />
              <button className="sm-btn sm-btn-search" onClick={handleManualBarcode} disabled={!barcodeInput.trim()}>
                ОК
              </button>
            </div>

            {lastScanMsg && (
              <div className={'sm-status ' + (lastScanMsg.startsWith('✅') ? 'sm-status-ok' : 'sm-status-warn')}>
                {lastScanMsg}
              </div>
            )}

            <div className="rev-cis-list">
              {groupData.cis_group.map(item => (
                <div key={item.sync_id} className={'rev-cis-row ' + (confirmedCis.has(item.cis) ? 'rev-cis-ok' : 'rev-cis-pending')}>
                  {confirmedCis.has(item.cis) ? '✅' : '○'} {item.name}
                  {item.expiry_date ? ' · до ' + item.expiry_date.slice(0, 7) : ''}
                </div>
              ))}
            </div>

            <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
              <button
                className="sm-btn sm-btn-save"
                onClick={() => setStep('group')}
              >
                Завершить
              </button>
              <button
                className="sm-btn sm-btn-secondary"
                onClick={() => { setStep('group'); }}
              >
                Отмена
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default MedicineRevisionModal;
